import { DefaultError, useMutation } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IMusicProductDigitalImageResponse } from './types/query';
import i18next from 'i18next';

export const usePostMusicProductDigitalImage = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductDigitalImage'
  );
  return useMutation<IMusicProductDigitalImageResponse, DefaultError, {file: File, id: string}>({
    mutationFn: async ({file, id}) => {
      loading(
        i18next.t('service-message.add.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );
      const formData = new FormData();

      formData.append('file', file);

      const response = await apiClient<FormData>({
        method: 'POST',
        url: `MusicProductDigital/Image`,
        body: formData,
        params: { mpdid: id },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductDigitalImage',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.add.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );
    },
    onError: dismiss
  });
};

export const usePutMusicProductDigitalImage = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutMusicProductDigitalImage'
  );
  return useMutation<{}, DefaultError, File>({
    mutationFn: async file => {
      loading(
        i18next.t('service-message.change.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );

      const formData = new FormData();

      formData.append('file', file);

      const response = await apiClient<FormData>({
        method: 'PUT',
        url: `MusicProductDigital/Image`,
        body: formData,
        params: { mpdid: id },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePutMusicProductDigitalImage',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.change.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );
    },
    onError: dismiss
  });
};

export const useDeleteMusicProductDigitalImage = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteMusicProductDigitalImage'
  );
  return useMutation<{}, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );

      const response = await apiClient<FormData>({
        method: 'DELETE',
        url: `MusicProductDigital/Image/${id}`
      });

      return responseHandler(response, {
        groupName: 'useDeleteMusicProductDigitalImage',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );
    },
    onError: dismiss
  });
};
